import { Container, Graphics, Text, TextMetrics, TextStyle } from "pixi.js"

import { BasicSprite } from "../../sprites/basic_sprite"
import { HELP_ENTRIES } from "../help_entries"
import { BuilderPage, PageId } from "./page"
import { ACCENT, BODY_H, BODY_Y, CONTENT_W, RIM, TEXT, CARD_FILL, CARD_RIM } from "./theme"
import { drawGlassPanel, sectionHeading, text } from "./widgets"

// The pages either side of the forge: the exile that puts you on the road, the briefing that sends
// you off, and the controls reference.

const LINE_H = 20
const TYPE_MS_PER_CHAR = 16

// A block of prose that types itself out. Pre-wrapped with TextMetrics so revealing a character at a
// time can't reflow the paragraph underneath itself (the usual word-wrap typewriter jitter).
class Typewriter extends Container {
    private lines: Text[] = []
    private total = 0
    private revealed = 0
    private elapsed = 0
    private finished = false

    constructor(body: string, width: number, { size = 13, color = TEXT }: { size?: number; color?: number } = {}) {
        super()
        const style = new TextStyle({
            fontFamily: "Verdana, Geneva, sans-serif",
            fontSize: size,
            wordWrap: true,
            wordWrapWidth: width,
            // Match what text() below actually renders, so the lines it measures are the lines drawn.
            strokeThickness: 0,
        })
        const measured = TextMetrics.measureText(body, style)
        measured.lines.forEach((line, i) => {
            const label = text("", { size, color })
            label.y = i * LINE_H
            this.addChild(label)
            this.lines.push(label)
            this.total += line.length
            // Stash the finished line on the object so the reveal doesn't have to re-measure.
            ;(label as any)._full = line
        })
    }

    animate(dtMs: number) {
        if (this.finished) {
            return
        }
        this.elapsed += dtMs
        const target = Math.floor(this.elapsed / TYPE_MS_PER_CHAR)
        if (target <= this.revealed) {
            return
        }
        this.revealed = Math.min(target, this.total)
        let remaining = this.revealed
        for (let i = 0; i < this.lines.length; i++) {
            const full: string = (this.lines[i] as any)._full
            const take = Math.max(0, Math.min(full.length, remaining))
            if (this.lines[i].text.length !== take) {
                this.lines[i].text = full.slice(0, take)
            }
            remaining -= full.length
        }
        if (this.revealed >= this.total) {
            this.finished = true
        }
    }
}

export class IntroPage extends BuilderPage {
    readonly pageId: PageId = "intro"
    readonly title = "The long exile"

    private typewriter?: Typewriter

    animate(dtMs: number) {
        this.typewriter?.animate(dtMs)
    }

    build() {
        this.wipe()
        this.typewriter = undefined

        const panelW = 560 + (CONTENT_W - 780) * 0.75
        const panelX = (CONTENT_W - panelW) / 2
        const panelH = 190
        const panelY = BODY_Y + (BODY_H - panelH) / 2

        const panel = new Graphics()
        drawGlassPanel(panel, panelX, panelY, panelW, panelH, { radius: 12 })
        this.addChild(panel)

        const rule = new Graphics()
        rule.beginFill(RIM, 0.8)
        rule.drawRect(panelX + 28, panelY + 34, 44, 2)
        rule.endFill()
        this.addChild(rule)

        const kicker = text("BY ORDER OF THE EMPEROR", { size: 10, color: ACCENT, bold: true })
        kicker.x = panelX + 28
        kicker.y = panelY + 18
        kicker.alpha = 0.9
        this.addChild(kicker)

        const body = new Typewriter(
            "You have been exiled to the barbarous lands of the West, for the Emperor will suffer no rivals in the East.",
            panelW - 56,
            { size: 14 },
        )
        body.x = panelX + 28
        body.y = panelY + 54
        this.addChild(body)
        this.typewriter = body

        this.host.setNavButtons([
            {
                title: "Forge your character ▸",
                primary: true,
                callback: () => this.host.switchPage("build"),
            },
        ])
    }
}

// The loop of an ember camp, in the order a player meets it: the fire that keeps the Lord alive,
// the food that buys a household, and the assault that comes for both. These are the same three
// things the end-of-run scorecard counts, so the briefing and the reckoning agree.
const STEPS = [
    "Fell trees and feed the wood to the fire. Keep it burning.",
    "Hunt meat in the forest. Hungry strangers will be drawn in. Task them to aid in your survival.",
    "The dead come in waves. Keep the fire lit, and hold out for as long as you can.",
]

export class JourneyPage extends BuilderPage {
    readonly pageId: PageId = "journey"
    readonly title = "Journey onward"

    build() {
        this.wipe()

        const panelW = 470 + (CONTENT_W - 780) * 0.65
        const panelX = 24
        const panel = new Graphics()
        drawGlassPanel(panel, panelX, BODY_Y, panelW, BODY_H, { radius: 12 })
        this.addChild(panel)

        // Wrapped, and everything below it hung off its measured height: the lead is two lines of
        // prose rather than a caption, and an unwrapped one would run out over the campfire.
        const lead = text(
            "You are the steward of a great house. Your lord is dying. The fire is the only thing anchoring him to the word.",
            {
                size: 12,
                color: TEXT,
                wrap: panelW - 44,
            },
        )
        lead.x = panelX + 22
        lead.y = BODY_Y + 18
        this.addChild(lead)

        const heading = sectionHeading("Holding the camp", panelW - 44)
        heading.x = panelX + 22
        heading.y = lead.y + lead.height + 14
        this.addChild(heading)

        let y = heading.y + 22
        STEPS.forEach((step, i) => {
            const card = new Graphics()
            card.lineStyle(1, CARD_RIM, 0.6)
            card.beginFill(CARD_FILL, 0.85)
            card.drawRoundedRect(panelX + 22, y, panelW - 44, 48, 8)
            card.endFill()
            card.lineStyle(0)
            // Numbered badge.
            card.beginFill(RIM, 0.9)
            card.drawCircle(panelX + 46, y + 24, 12)
            card.endFill()
            this.addChild(card)

            const number = text(`${i + 1}`, { size: 12, color: 0x2a1c0c, bold: true })
            number.x = panelX + 46 - number.width / 2
            number.y = y + 24 - number.height / 2
            this.addChild(number)

            // Wrapped inside the card, to the right of its numbered badge; the vertical centring
            // below already works off the measured height, so a second line costs nothing.
            const body = text(step, { size: 11, color: TEXT, wrap: panelW - 112 })
            body.x = panelX + 68
            body.y = y + 24 - body.height / 2
            this.addChild(body)

            y += 56
        })

        const closer = text("Survive.", {
            size: 11,
            color: ACCENT,
            wrap: panelW - 44,
        })
        closer.x = panelX + 22
        closer.y = BODY_Y + BODY_H - 18 - closer.height
        this.addChild(closer)

        this.drawCampfire(panelX + panelW + 90, BODY_Y + BODY_H / 2)
    }

    // The campfire the briefing is about, burning beside it.
    private drawCampfire(x: number, y: number) {
        const halo = new Graphics()
        halo.beginFill(0xff8a24, 0.08)
        halo.drawCircle(x, y, 96)
        halo.endFill()
        halo.beginFill(0xffcf5a, 0.07)
        halo.drawCircle(x, y, 58)
        halo.endFill()
        this.addChild(halo)

        const campfire = new BasicSprite({ name: "lpc-terrain:760", scale: 1.0, color: 0xffffff })
        const fire = new BasicSprite({ name: "fire", scale: 1.0, color: 0xffffff })
        fire.x = 3
        fire.y = -23

        const animation = new Graphics()
        animation.addChild(campfire)
        animation.addChild(fire)
        animation.scale.set(3.0)
        animation.x = x
        animation.y = y
        this.addChild(animation)
    }
}

export class ControlsPage extends BuilderPage {
    readonly pageId: PageId = "controls"
    readonly title = "Controls"

    build() {
        this.wipe()

        const panelW = CONTENT_W - 140
        const panelX = (CONTENT_W - panelW) / 2
        const panel = new Graphics()
        drawGlassPanel(panel, panelX, BODY_Y, panelW, BODY_H, { radius: 12 })
        this.addChild(panel)

        // Two columns of entries, so nothing runs off the bottom of the panel.
        const colW = (panelW - 66) / 2
        const rows = Math.ceil(HELP_ENTRIES.length / 2)
        HELP_ENTRIES.forEach((entry, i) => {
            const col = Math.floor(i / rows)
            const row = i % rows
            const x = panelX + 24 + col * (colW + 18)
            const y = BODY_Y + 20 + row * 62

            const title = text(entry.title, { size: 11, color: ACCENT, bold: true })
            title.x = x
            title.y = y
            this.addChild(title)

            const body = text(entry.body, { size: 10, color: TEXT, wrap: colW })
            body.x = x
            body.y = y + 16
            this.addChild(body)
        })

        this.host.setNavButtons([
            {
                title: "◂ Back",
                callback: () => {
                    this.host.resetNavButtons()
                    this.host.switchPage("journey")
                },
            },
        ])
    }
}
